<template>
  <InvitationsDialog
    :open="invitationsOpen"
    :invitations="invitations"
    @close="invitationsOpen = false"
    @logout="$emit('logout')"
    @review="activeSetting = 'Invitations'"
  />
  <ApplicationShell
    v-if="selectedCustomerAvailable"
    :loading="loading"
    :error-message="errorMessage"
  >
    <section v-if="loading" class="d-flex justify-content-center align-items-center w-100 h-100">
      <AsciiSpinner class="fs-5" />
    </section>
    <router-view v-else v-slot="{Component}">
      <keep-alive exclude="NotFoundView">
        <component
          :is="Component"
          :key="route.name"
          @contact="$emit('contact')"
          @logout="$emit('logout')"
        />
      </keep-alive>
    </router-view>
  </ApplicationShell>
</template>
<script lang="ts" setup>
import {AxiosError} from 'axios';
import {settingsKey, SettingsKey} from '@/components/settings/injectionKeys';
import {feedbackOpenKey} from '@/components/shared/custom/FeedbackSlideIn/injectionKeys';

defineEmits<{contact: []; logout: []}>();
const route = useRoute();

// Allow the rest of Sphere to set the active setting.
const activeSetting = ref<SettingsKey>('');
provide(settingsKey, activeSetting);

// Feedback is a page of the settings: whoever asks to give feedback gets that
const handleFeedbackOpen = () => (activeSetting.value = 'Feedback');
provide(feedbackOpenKey, handleFeedbackOpen);

const customersStore = useCustomersStore();
const selectedCustomerAvailable = computed(
  () => customersStore.selectedCustomer?.customer?.debtor_number !== undefined,
);

const invitationsStore = useInvitationsStore();
const userStore = useUserStore();
const invitationsOpen = ref(false);
const invitations = computed(
  () => (userStore.user && invitationsStore.getUserInvitations(userStore.user?.email)) || [],
);
const checkInvitations = () => {
  // It's essential to force the user to either accept an invitation if no user relations are found, or log out.
  if (invitations.value.length >= 1 && route.name !== 'Page') {
    if (invitations.value.some((i) => i.key_expired === false)) {
      invitationsOpen.value = true;
    }
  }
};

const coresStore = useCoresStore();
const loading = ref(true);
const errorMessage = ref<string>();
const fetchData = async () => {
  loading.value = true;
  try {
    await Promise.all([coresStore.readPages(), userStore.readUserPermissions()]);
  } catch (error: any) {
    errorMessage.value = error instanceof AxiosError ? error.response?.data.detail : error;
  } finally {
    loading.value = false;
  }
};

onMounted(() => {
  checkInvitations();
  /**
   * From here we know what customers/invitations the user has.
   * If the user has no user relations but has invitations, don't
   * bother fetching the navigation.
   */
  if (selectedCustomerAvailable.value) fetchData();
});
</script>
